import { useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTeam } from 'app/frontend/base/config/context/team'
import { UpdateEventActivityPayload } from 'app/frontend/base/api/event'
import useReferenceStore from 'app/frontend/base/state/useReferenceStore'
import {
  captureTeamEventReferenceCaches,
  patchTeamEventReferenceCaches,
  restoreTeamEventReferenceCaches,
} from '../utils/teamEventReferenceCache'
import { unavailableParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'

type OptimisticEventSnapshot = {
  event?: wowaudit.Event
  eventReferenceCaches: ReturnType<typeof captureTeamEventReferenceCaches>
}

type OptimisticEventPatch = {
  id: number
  settled: boolean
  apply: (event: wowaudit.Event) => wowaudit.Event
}

type OptimisticEventMutationContext = OptimisticEventSnapshot & {
  optimisticPatchId?: number
}

type UpdateSelectionVariables = {
  eventParticipantId: number
  role?: string
  specId?: number | null
  characterId?: number
  selected?: boolean
  activityId?: number
}

type UpdateActivitySelectionVariables = {
  activityId: number
  selected: boolean
}

type UpdateAllActivitiesSelectionVariables = {
  activityIds: number[]
  selected: boolean
}

type UpdateActivityVariables = {
  activityId: number
  eventActivity: UpdateEventActivityPayload
  // The resulting overrides, needed because the request may only carry a patch.
  optimisticPlanOverrides?: wowaudit.EventActivity['planOverrides']
}

type UpdateActivityOrderVariables = {
  activityOrder: number[]
}

const useOptimisticEventUpdate = (eventId?: number) => {
  const queryClient = useQueryClient()
  const { team } = useTeam()
  const { storeReferences } = useReferenceStore()
  const pendingPatchesRef = useRef<OptimisticEventPatch[]>([])
  const nextPatchIdRef = useRef(1)

  const teamEventQueryKey = eventId
    ? (['teamEvent', { teamId: team.id, eventId }] as const)
    : null

  const snapshot = async (): Promise<OptimisticEventSnapshot> => {
    await Promise.all([
      teamEventQueryKey
        ? queryClient.cancelQueries({ queryKey: teamEventQueryKey })
        : Promise.resolve(),
      queryClient.cancelQueries({
        queryKey: ['teamEvents', { id: team.id, type: 'overview' }],
      }),
      queryClient.cancelQueries({
        queryKey: ['teamEvents', { id: team.id, type: 'calendar' }],
      }),
    ])

    return {
      event: teamEventQueryKey
        ? queryClient.getQueryData<wowaudit.Event>(teamEventQueryKey)
        : undefined,
      eventReferenceCaches: captureTeamEventReferenceCaches(
        queryClient,
        team.id,
      ),
    }
  }

  const applyPatchedEvent = (event: wowaudit.Event) => {
    if (teamEventQueryKey) {
      queryClient.setQueryData(teamEventQueryKey, event)
    }
    patchTeamEventReferenceCaches(queryClient, team.id, event)
  }

  const replayOptimisticPatches = (event: wowaudit.Event) =>
    pendingPatchesRef.current.reduce(
      (nextEvent, patch) => patch.apply(nextEvent),
      event,
    )

  const clearSettledOptimisticPatches = () => {
    if (pendingPatchesRef.current.every((patch) => patch.settled)) {
      pendingPatchesRef.current = []
    }
  }

  const registerOptimisticPatch = (
    previous: OptimisticEventSnapshot,
    apply: (event: wowaudit.Event) => wowaudit.Event,
  ): OptimisticEventMutationContext => {
    const optimisticPatchId = nextPatchIdRef.current
    nextPatchIdRef.current += 1
    pendingPatchesRef.current = [
      ...pendingPatchesRef.current,
      { id: optimisticPatchId, settled: false, apply },
    ]
    return { ...previous, optimisticPatchId }
  }

  const settleOptimisticPatch = (optimisticPatchId?: number) => {
    if (optimisticPatchId === undefined) return
    pendingPatchesRef.current = pendingPatchesRef.current.map((patch) =>
      patch.id === optimisticPatchId ? { ...patch, settled: true } : patch,
    )
  }

  const removeOptimisticPatch = (optimisticPatchId?: number) => {
    if (optimisticPatchId === undefined) return
    pendingPatchesRef.current = pendingPatchesRef.current.filter(
      (patch) => patch.id !== optimisticPatchId,
    )
  }

  const applyServerUpdate = (
    event: wowaudit.Event,
    context?: OptimisticEventMutationContext,
  ) => {
    settleOptimisticPatch(context?.optimisticPatchId)
    const nextEvent = replayOptimisticPatches(event)
    applyPatchedEvent(nextEvent)
    storeReferences([nextEvent])
    clearSettledOptimisticPatches()
  }

  const rollback = (previous: OptimisticEventMutationContext | undefined) => {
    if (!previous) return
    removeOptimisticPatch(previous.optimisticPatchId)
    restoreTeamEventReferenceCaches(queryClient, previous.eventReferenceCaches)
    if (previous.event) {
      applyPatchedEvent(replayOptimisticPatches(previous.event))
      clearSettledOptimisticPatches()
      return
    }
    clearSettledOptimisticPatches()
  }

  const optimisticStatusUpdate = async ({
    eventParticipantId,
    status,
  }: {
    eventParticipantId: number
    status: wowaudit.EventParticipantReference['status']
  }) => {
    const previous = await snapshot()
    if (!previous.event) return previous

    const apply = (event: wowaudit.Event): wowaudit.Event => ({
      ...event,
      participants: event.participants.map((participant) =>
        participant.id === eventParticipantId
          ? { ...participant, status }
          : participant,
      ),
      participantReferences: event.participantReferences.map(
        (participantReference) =>
          participantReference.id === eventParticipantId
            ? { ...participantReference, status }
            : participantReference,
      ),
    })
    const context = registerOptimisticPatch(previous, apply)
    applyPatchedEvent(apply(previous.event))
    return context
  }

  const optimisticAdminCommentUpdate = async ({
    eventParticipantId,
    adminComment,
  }: {
    eventParticipantId: number
    adminComment: string
  }) => {
    const previous = await snapshot()
    if (!previous.event) return previous

    const apply = (event: wowaudit.Event): wowaudit.Event => ({
      ...event,
      participants: event.participants.map((participant) =>
        participant.id === eventParticipantId
          ? { ...participant, adminComment }
          : participant,
      ),
      participantReferences: event.participantReferences.map(
        (participantReference) =>
          participantReference.id === eventParticipantId
            ? { ...participantReference, adminComment }
            : participantReference,
      ),
    })
    const context = registerOptimisticPatch(previous, apply)
    applyPatchedEvent(apply(previous.event))
    return context
  }

  const optimisticSelectionUpdate = async ({
    eventParticipantId,
    role,
    specId,
    characterId,
    selected,
    activityId,
  }: UpdateSelectionVariables) => {
    const previous = await snapshot()
    if (!previous.event) return previous

    const apply = (event: wowaudit.Event): wowaudit.Event => ({
      ...event,
      participants: event.participants.map((participant) => {
        if (participant.id !== eventParticipantId) return participant

        const nextParticipant: wowaudit.EventParticipant = { ...participant }
        if (activityId === undefined) {
          if (role !== undefined) {
            nextParticipant.role = role as wowaudit.EventParticipant['role']
          }
          if (specId !== undefined) {
            if (specId === null) {
              delete nextParticipant.specId
            } else {
              nextParticipant.specId = specId
            }
          }
          if (characterId !== undefined) {
            nextParticipant.altCharacterId = characterId
            nextParticipant.altCharacterReference = team.members?.find(
              (member) => member.characterReference.id === characterId,
            )?.characterReference
          }
        }

        const nextSelections = { ...nextParticipant.selections }
        const applySelection = (targetActivityId: number) => {
          const key = targetActivityId.toString()
          const currentSelection = nextSelections[key] ?? {}
          const updatedSelection = { ...currentSelection }
          if (selected !== undefined) updatedSelection.selected = selected
          if (role !== undefined) updatedSelection.role = role
          if (specId !== undefined) updatedSelection.spec_id = specId
          if (characterId !== undefined)
            updatedSelection.character_id = characterId
          nextSelections[key] = updatedSelection
        }

        if (activityId !== undefined) {
          applySelection(activityId)
        } else if (
          selected !== undefined ||
          role !== undefined ||
          specId !== undefined ||
          characterId !== undefined
        ) {
          event.activities.forEach((activity) => applySelection(activity.id))
        }

        nextParticipant.selections = nextSelections
        return nextParticipant
      }),
      participantReferences: event.participantReferences.map(
        (participantReference) => {
          if (participantReference.id !== eventParticipantId)
            return participantReference

          const nextParticipantReference: wowaudit.EventParticipantReference = {
            ...participantReference,
          }
          if (activityId === undefined) {
            if (role !== undefined) {
              nextParticipantReference.role =
                role as wowaudit.EventParticipantReference['role']
            }
            if (specId !== undefined) {
              if (specId === null) {
                delete nextParticipantReference.specId
              } else {
                nextParticipantReference.specId = specId
              }
            }
            if (characterId !== undefined) {
              nextParticipantReference.altCharacterId = characterId
              nextParticipantReference.altCharacterReference =
                team.members?.find(
                  (member) => member.characterReference.id === characterId,
                )?.characterReference
            }
          }
          return nextParticipantReference
        },
      ),
    })

    const context = registerOptimisticPatch(previous, apply)
    applyPatchedEvent(apply(previous.event))
    return context
  }

  const optimisticActivitySelectionUpdate = async ({
    activityId,
    selected,
  }: UpdateActivitySelectionVariables) => {
    const previous = await snapshot()
    if (!previous.event) return previous

    const apply = (event: wowaudit.Event): wowaudit.Event => ({
      ...event,
      participants: event.participants.map((participant) => {
        if (unavailableParticipantStatuses.includes(participant.status)) {
          return participant
        }

        return {
          ...participant,
          selections: {
            ...participant.selections,
            [activityId]: {
              ...(participant.selections[activityId] ?? {}),
              selected,
            },
          },
        }
      }),
    })
    const context = registerOptimisticPatch(previous, apply)
    applyPatchedEvent(apply(previous.event))
    return context
  }

  const optimisticAllActivitiesSelectionUpdate = async ({
    activityIds,
    selected,
  }: UpdateAllActivitiesSelectionVariables) => {
    const previous = await snapshot()
    if (!previous.event) return previous

    const apply = (event: wowaudit.Event): wowaudit.Event => ({
      ...event,
      participants: event.participants.map((participant) => {
        if (unavailableParticipantStatuses.includes(participant.status)) {
          return participant
        }

        const nextSelections = { ...participant.selections }
        activityIds.forEach((activityId) => {
          nextSelections[activityId] = {
            ...(nextSelections[activityId] ?? {}),
            selected,
          }
        })

        return { ...participant, selections: nextSelections }
      }),
    })
    const context = registerOptimisticPatch(previous, apply)
    applyPatchedEvent(apply(previous.event))
    return context
  }

  const optimisticActivityUpdate = async ({
    activityId,
    eventActivity,
    optimisticPlanOverrides,
  }: UpdateActivityVariables) => {
    const previous = await snapshot()
    if (!previous.event) return previous

    const event = previous.event
    const nextPlanOverrides =
      optimisticPlanOverrides ?? eventActivity.planOverrides
    const normalizedActivityUpdate: Partial<wowaudit.EventActivity> = {
      ...(eventActivity.active === undefined
        ? {}
        : { active: eventActivity.active }),
      ...(eventActivity.order === undefined
        ? {}
        : { order: eventActivity.order }),
      ...(eventActivity.linkedPlans === undefined
        ? {}
        : { linkedPlans: eventActivity.linkedPlans }),
      ...(nextPlanOverrides === undefined
        ? {}
        : { planOverrides: nextPlanOverrides }),
    }
    const nextEvent: wowaudit.Event = {
      ...event,
      activities: event.activities.map((activity) =>
        activity.id === activityId
          ? { ...activity, ...normalizedActivityUpdate }
          : activity,
      ),
    }
    applyPatchedEvent(nextEvent)
    return previous
  }

  const optimisticActivityOrderUpdate = async ({
    activityOrder,
  }: UpdateActivityOrderVariables) => {
    const previous = await snapshot()
    if (!previous.event) return previous

    const apply = (event: wowaudit.Event): wowaudit.Event => {
      const orderByActivityId = new Map<number, number>()
      activityOrder.forEach((activityId, index) => {
        orderByActivityId.set(activityId, index)
      })
      const sortedActivities = event.activities
        .slice()
        .sort((left, right) => left.order - right.order)
      const missingActivityIds = sortedActivities
        .map((activity) => activity.id)
        .filter((activityId) => !orderByActivityId.has(activityId))
      missingActivityIds.forEach((activityId, index) => {
        orderByActivityId.set(activityId, activityOrder.length + index)
      })
      return {
        ...event,
        activities: event.activities.map((activity) => ({
          ...activity,
          order: orderByActivityId.get(activity.id) as number,
        })),
      }
    }
    const context = registerOptimisticPatch(previous, apply)
    applyPatchedEvent(apply(previous.event))
    return context
  }

  return {
    optimisticStatusUpdate,
    optimisticAdminCommentUpdate,
    optimisticSelectionUpdate,
    optimisticActivitySelectionUpdate,
    optimisticAllActivitiesSelectionUpdate,
    optimisticActivityUpdate,
    optimisticActivityOrderUpdate,
    applyServerUpdate,
    rollback,
  }
}

export default useOptimisticEventUpdate
